import React, { useEffect, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import "./HeaderSelector.css";

interface DisplayValue<T> {
  display: string;
  value: T;
  icon?: React.ReactNode;
  canBeClicked?: boolean;
  onClick?: () => void;
}
type Props<T> = {
  value: T;
  onChange: (value: T) => void;
  displayValues: DisplayValue<T>[];
  noArrow?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
};

type ContentProps = {
  options: React.ReactNode;
  onOutsideClick: () => void;
  style?: React.CSSProperties;
};

// const isDomParent = (child: Node, parent: Node) => {
//   let checkDom: Node | null = child;
//   while (checkDom) {
//     if ((checkDom as Element).innerHTML === (parent as Element).innerHTML) {
//       return true;
//     }
//     checkDom = checkDom.parentNode;
//   }
//   return false;
// };

const isDomParent = (child: Node, parent: Node) => {
  return parent.contains(child) || parent.parentNode?.contains(child);
};

function DropDownContent({
  options,
  onOutsideClick = () => {},
  style = {},
}: ContentProps) {
  const container = useRef(null);
  const onOutsieClicked = (event: MouseEvent) => {
    const containerDom = container.current
      ? (container.current as unknown)
      : {};
    const targetDom = event.target;
    const check = isDomParent(targetDom as Node, containerDom as Node);
    if (check) {
      event.preventDefault();
    } else {
      onOutsideClick();
    }
  };

  const savedHandler = useRef(onOutsieClicked);

  useEffect(() => {
    const eventListener = (event: any) => savedHandler.current(event);
    document.addEventListener("mousedown", eventListener);
    return () => document.removeEventListener("mousedown", eventListener);
  }, []);

  return (
    <div className={"dropdown-content dropdown-content-active"} ref={container} style={style}>
      {options}
    </div>
  );
}

export default function HeaderSelector<T extends string | number>({
  value,
  onChange,
  displayValues,
  noArrow = false,
  disabled = false,
  style = {},
  contentStyle = {},
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const selected = displayValues.filter(
    (displayValue) => displayValue.value === value
  )[0];
  const display = selected ? selected.display : "";
  const icon = selected ? selected.icon : undefined;
  const customWidth =
    typeof style.width === "number" ? parseInt(style.width.toString()) : 237;
  const customToolTipThreshold = Math.round((customWidth / 237) * 15);
  const charThresholdForToolTip =
    customWidth === 237
      ? icon === undefined || icon === null
        ? 15
        : 10
      : icon === undefined || icon === null
      ? customToolTipThreshold
      : customToolTipThreshold + 5;

  const options = displayValues.map((displayValue) => (
    <p
      onClick={() => {
        if(displayValue.onClick){
          displayValue.onClick();
        }

        if(displayValue.canBeClicked !== undefined){
          if(!displayValue.canBeClicked){
            return;
          }
        }
        onChange(displayValue.value);
        setOpen(false);
      }}
    >
      {displayValue.display}
    </p>
  ));

  const selectStyle: React.CSSProperties = {
    color: disabled ? "gray" : "white",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <div className="header-selector-container">
      {!noArrow && (
        <div className="right-arrow">
          <svg
            width="10"
            height="16"
            viewBox="0 0 10 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.52393 8L0.130822 15.7942L0.130823 0.205771L9.52393 8Z"
              fill={disabled ? "gray" : "white"}
            />
          </svg>
        </div>
      )}
      <div
        className="dropdown"
        onBlur={() => setOpen(false)}
        data-for="common-tooltip"
        data-tip={display}
        data-tip-disable={
          display.length <= charThresholdForToolTip ||
          open
        }
        data-effect="solid"
        data-place="bottom"
        style={style}
      >
        <div className="dropdown-title">
          {icon !== undefined && icon !== null && (
            <div className="dropdown-icon">{icon}</div>
          )}

          <span style={selectStyle}>{display}</span>

          <svg
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              setOpen(!open && !disabled);
              ReactTooltip.hide();
            }}
            className="dropdown-button"
          >
            <rect
              x="-0.5"
              y="0.5"
              width="33"
              height="33"
              rx="5.5"
              transform="matrix(-1 0 0 1 33 0)"
              fill="#808080"
              stroke="#808080"
            />
            <path
              d="M22 14L17 20L12 14H15.125H15.75H18.25H18.875H22Z"
              fill={disabled ? "#A0A0A0" : "white"}
            />
          </svg>
        </div>

        {open && (
          <DropDownContent
            options={options}
            onOutsideClick={() => setOpen(false)}
            style={contentStyle}
          />
        )}
      </div>
    </div>
  );
}
